import { useEffect } from 'react'

import { useDebounce } from 'use-debounce'

import { locationEvent, pageViewEvent } from 'src/Helpers/TagManager'
import { pageInfo } from 'src/Helpers/TagManager/pageInfo'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { getIsAuth } from 'src/Redux/auth-process/credentialsSlice/selectors'
import {
  getCampaigns,
  getCampaignsLoader,
} from 'src/Redux/campaignList-process'
import { getCampaignsList } from 'src/Redux/campaignList-process/api-actions'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
  CampaignTypeEnum,
  SpotifyCampaign,
  TiktokCampaign,
} from 'src/Types/campaigns'
import {
  getCampaignsFilters,
  getCampaignsSearch,
} from 'src/Redux/campaignsActions-process'
import { DEBOUNCE_DELAY_1000 } from 'src/Constants/constants'
import { useReels } from 'src/Hooks/useReels'
import { MetaCampaign } from 'src/Redux/ad-campaignApi'

import { getStatusText } from './helpers'

export interface CampaignUnionType {
  trackImage?: string
  trackName?: string
  artistName?: string
  platform: CampaignPlatformTypeEnum
  status: CampaignStatusEnum
  statusText?: string
  id: number
  relaunched?: boolean
  isReels?: boolean
  matchingType?: string
  isPreRelease?: boolean
  releaseDate?: string
  placementsCount?: number
  trackSpotifyId?: string | null
  type?: CampaignTypeEnum
}

interface CampaignsHook {
  campaigns: Array<CampaignUnionType>
  loading: boolean
  hasCampaigns: boolean
}

const normalizeSearchValue = (value?: string): string =>
  value?.normalize('NFC').toLowerCase().replace(/\s+/g, ' ').trim() ?? ''

const transformCampaigns = (
  campaigns: Array<SpotifyCampaign | TiktokCampaign | MetaCampaign>,
  isReelsUser: boolean,
): Array<CampaignUnionType> =>
  campaigns.map((campaign) => ({
    trackImage: campaign.trackCoverUrl ?? '',
    trackName: campaign.trackTitle,
    artistName: campaign.trackAuthorName,
    platform: campaign.platformType,
    isReels:
      campaign.platformType === CampaignPlatformTypeEnum.TIKTOK &&
      isReelsUser &&
      campaign.type === CampaignTypeEnum.FIRST,
    status: campaign.status as CampaignStatusEnum,
    id: Number(campaign.id),
    relaunched: campaign.relaunched,
    matchingType: 'matchingType' in campaign ? campaign.matchingType : '',
    ...('type' in campaign && {
      type: campaign.type,
    }),
    ...('isPreRelease' in campaign && {
      isPreRelease: campaign.isPreRelease,
      releaseDate: campaign.releaseDate,
      placementsCount: campaign.placementsCount,
      trackSpotifyId: campaign.trackSpotifyId,
    }),
    statusText: getStatusText({
      status: campaign.status,
      allReview: campaign.reviewedReviews,
      platformType: campaign.platformType,
      submittedVideosAmount: campaign.submittedVideosAmount,
      startDate: campaign.startDate,
    }),
  }))

export const useCampaigns = (): CampaignsHook => {
  const dispatch = useAppDispatch()
  const userID = useAppSelector(getUserId)
  const campaigns = useAppSelector(getCampaigns)
  const loading = useAppSelector(getCampaignsLoader)
  const filters = useAppSelector(getCampaignsFilters)
  const search = useAppSelector(getCampaignsSearch)
  const [searchValue] = useDebounce(search, DEBOUNCE_DELAY_1000)

  const isAuth = useAppSelector(getIsAuth)
  const { isReelsUser } = useReels()

  useEffect(() => {
    if (!isAuth) {
      return
    }
    void dispatch(getCampaignsList())
  }, [dispatch, isAuth])

  useEffect(() => {
    if (userID) {
      pageViewEvent({ userID, currentPageInfo: pageInfo.campaigns })
      locationEvent()
    }
  }, [userID])

  const filterCampaigns = (): CampaignUnionType[] => {
    let campaignsList = transformCampaigns(campaigns, isReelsUser)
    if (searchValue) {
      const normalizedSearch = normalizeSearchValue(searchValue)
      campaignsList = campaignsList.filter(
        (campaign) =>
          normalizeSearchValue(campaign?.artistName).includes(
            normalizedSearch,
          ) ||
          normalizeSearchValue(campaign?.trackName).includes(normalizedSearch),
      )
    }

    const anyFilterSelected = filters.spotify || filters.tiktok || filters.ads

    if (anyFilterSelected) {
      campaignsList = campaignsList.filter((campaign) => {
        const isSpotify =
          filters.spotify &&
          campaign.platform === CampaignPlatformTypeEnum.SPOTIFY

        const isTiktok =
          filters.tiktok &&
          campaign.platform === CampaignPlatformTypeEnum.TIKTOK

        const isMeta =
          filters.ads && campaign.platform === CampaignPlatformTypeEnum.META

        return isSpotify || isTiktok || isMeta
      })
    }

    return campaignsList
  }

  return {
    campaigns: filterCampaigns(),
    hasCampaigns: campaigns.length > 0,
    loading,
  }
}
